import { Editor, useEditor, useEditorState } from '@tiptap/react';
import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { history } from '@tiptap/pm/history';

import { TDocument, TDraft, TVariant, TVersion, TLockedState } from '../types';
import { StatusLabel, TScrollbarElem } from '@shared/components';
import { TTreeHeading } from '../tiptap/nodes/heading/HeadingNode';
import useOptions from '../hooks/useOptions.hook';
import { TContentStatus, useContent, useGetContent } from '../api';
import { useLeaveGuard, useSearchParams, useWebsocket } from '@shared/hooks';
import { ToastContext } from '@shared/contexts';
import { SignInContext, useLog } from '@modules/users';
import useAutoSave, { TSave } from '../hooks/useAutoSave.hook';
import {
  hasPendingEditorOperations,
  subscribePendingEditorOperations,
} from '../helpers/pendingEditorOperations.helper';
import LockedDialog from '../components/dialogs/LockedDialog';
import useView from '../hooks/useView.hook';
import {
  AUTO_EDIT_QUERY_PARAM,
  AUTO_EDIT_QUERY_VALUE,
  getVersionId,
  CreateVersionDialog,
  TResolvedItemsMap,
  TResolvedItemNames,
} from '@modules/items';
import { TPermissions } from '@modules/access';
import DocumentContainer from '../components/DocumentContainer';
import insertSpaceIntoEmptyTags from '../helpers/insertSpaceIntoEmptyTags.helper';
import hasUnsupportedMarkup from '../helpers/hasUnsupportedMarkup.helper';
import { makeStyles } from '@fluentui/react-components';
import { toggleTableResizing } from '../tiptap/nodes/table/TableNode';
import { TReplica } from '@modules/replicas';
import { GENERIC_ERROR_MESSAGE, TERMINOLOGY } from '@shared/i18n';
import { useLocation } from 'react-router-dom';
import useIsMounted from '../hooks/useIsMounted.hook';
import React from 'react';
import { loadDocument, releaseDocument, setRequestEdit } from '../largeDocument';
import { useWholeDocumentCaptions } from '../largeDocument';
import useHandleRequestDraft from '../hooks/useHandleRequestDraft.hook';
import useEditorContentReady from '../hooks/useEditorContentReady.hook';
import useTemplateDropdownOptions from '../hooks/useTemplateDropdownOptions.hook';
import { TMap } from '@shared/types';
import { TCaptionMap } from '@shared/backendFrontend/captionHelpersFe';
import { OutRefBookmarkKind } from '../tiptap/nodes/caption/constants';
import { COMPLETE_WHILE_CONNECTING_FAILED_MESSAGE } from '../constants';
import { EDocumentConfigTab } from '../components/dialogs/ItemConfigDialog/ItemConfigDialog';
import { isAiEditReviewActive } from '../tiptap/plugins/AiEditReviewPlugin';
import { useDocumentSavingStore } from '../stores/useDocumentSavingStore';

export const staticEditor = {
  editor: null,
};

type TDocumentContext = {
  doc: TDocument;
  permissions: TPermissions;
  resolvedItems: TResolvedItemsMap;
  resolvedItemNames: TResolvedItemNames;
  replica?: TReplica;

  editor: Editor;
  captionsMap: TCaptionMap | undefined;
  /** Whether `captionsMap` already covers the whole document, not only the part the editor holds. */
  wholeCaptionsReady: boolean;
  resolveCaptionLabel: (kind: OutRefBookmarkKind) => string;
  treeHeadings: TTreeHeading[];
  contentStatus: TContentStatus;
  /** The stored html holds markup the editor would drop, so it must not be edited. */
  hasUnsupportedContent: boolean;
  content: string | undefined;
  isContentReady: boolean;
  refContentNode: RefObject<TScrollbarElem>;
  refTooltip: HTMLElement | null;
  version?: TVersion | TDraft;
  versionId?: string;
  variant?: TVariant;

  isEdit: boolean;
  requestEdit: boolean;
  isAutoEditPending: boolean;
  hasDraft: boolean;
  save: TSave;
  isExplicitSavePending: boolean;
  editDraft: () => void;
  viewMode: () => Promise<boolean>;
  completeVersion: () => void;
  prepareComplete: () => void;
  completeWhileEditing: <T>(request: (editingDocumentId?: string) => Promise<T>) => Promise<T>;
  changeVariant: (variant?: TVariant) => Promise<void>;

  consumeAiEditSelectionReminder: () => boolean;

  isTableMenuOpen: boolean;
  setIsTableMenuOpen: Dispatch<SetStateAction<boolean>>;

  isImageMenuOpen: boolean;
  setIsImageMenuOpen: Dispatch<SetStateAction<boolean>>;

  openDocumentConfigRef: React.MutableRefObject<((initialTab?: EDocumentConfigTab) => void) | null>;

  templateDropdownOptionsRef: MutableRefObject<Record<string, string[]>>;
};

// @ts-ignore
const DocumentContext = createContext<TDocumentContext>(null);
export default DocumentContext;

type TProps = {
  doc: TDocument;
  permissions: TPermissions;
  resolvedItems: TResolvedItemsMap;
  resolvedItemNames: TResolvedItemNames;
  replica?: TReplica;
  // What renders inside the session; DocumentContainer when left out, as on the Document page.
  children?: ReactNode;
};

const useStyles = makeStyles({
  tooltipContainer: {
    position: 'fixed',
    zIndex: 9999,
    width: '300px',
  },
  tooltip: {
    '& div': {
      width: 'fit-content',
      maxWidth: '290px',
    },
    '& div div': {
      maxHeight: '200px',
      overflowY: 'auto',
    },
  },
});

export const DocumentProvider = React.memo(
  ({ doc, permissions, resolvedItems, resolvedItemNames, replica, children }: TProps) => {
    const { show } = useContext(ToastContext);
    const { change, changeMany, searchParams } = useSearchParams();
    const { user, organization } = useContext(SignInContext);

    const view = searchParams.get('view');
    const autoEdit = searchParams.get(AUTO_EDIT_QUERY_PARAM) === AUTO_EDIT_QUERY_VALUE;
    const [transitioningVariant, setTransitioningVariant] = useState<string | null>(null);

    // Refs
    const refEditor = useRef<Editor | null>(null);
    const refBlockAutoSave = useRef(true);
    const openDocumentConfigRef = useRef<((initialTab?: EDocumentConfigTab) => void) | null>(null);
    const refIsMounted = useIsMounted();
    const location = useLocation();
    const refDoc = useRef(doc);
    const refView = useRef(view);
    const refLocation = useRef(location);
    const refAutoEditStarted = useRef(false);

    // Sync for callbacks
    refDoc.current = doc;
    refView.current = view;
    refLocation.current = location;

    const { connected, retried, connect, disconnect } = useWebsocket(
      '',
      { userId: user._id, docId: doc._id },
      {
        onConnect: (retry) => {
          refBlockAutoSave.current = false;
          if (retry)
            // save after reconnect is done below
            show('success', 'Reconnected to Confideck editor.');
        },
        onConnectError: () => {
          show('error', 'Connecting to Confideck editor failed.');
          change('view');
        },
        onDisconnect: (expected) => {
          console.log(`Disconnected from Confideck editor (Expected: ${expected}).`);
          if (!expected) return show('error', 'Connection to Confideck editor is interrupted.');

          // clear history because of Mod-z bug: https://github.com/ueberdosis/tiptap/issues/5547
          refEditor.current?.unregisterPlugin('history');
          refEditor.current?.registerPlugin(history());

          // Leaving edit mode ends a page's edit intent too, in one write: writes build on the last rendered URL.
          if (refView.current === 'edit' && refIsMounted.current)
            changeMany({ view: undefined, [AUTO_EDIT_QUERY_PARAM]: undefined });
        },
        async onUnmount() {
          console.log('Save & disconnect on unmount');
          const editor = refEditor.current;
          const saving = save({ onFinished: disconnect });
          // React does not await this cleanup, so retire controller callbacks before Tiptap destroys the editor.
          if (editor) releaseDocument(editor);
          await saving;
        },
      }
    );
    const isEdit = connected && !transitioningVariant && !replica;

    // use view:
    const { requestEdit, variant, ...rest } = useView(doc, permissions, isEdit, retried);
    useEffect(() => setRequestEdit(requestEdit), [requestEdit]);
    const refIsEdit = useRef(isEdit);
    const refRequestEdit = useRef(requestEdit);
    refIsEdit.current = isEdit;
    refRequestEdit.current = requestEdit;
    const version = rest.version as TVersion | TDraft;
    const versionId = version ? getVersionId(version) : undefined;
    useEffect(() => {
      if (view === 'edit') return;
      if (isEdit) {
        console.log('Switching version view: Saving & disconnecting...');
        save({ onSuccess: disconnect });
      }
      if (!isEdit && retried) change('view', 'edit');
    }, [view]);

    // editor:
    const refAiEditReminderDocId = useRef<string>();
    const [treeHeadings, setTreeHeadings] = useState<TTreeHeading[]>([]);
    const [captionsMap, setCaptionsMap] = useState<TCaptionMap>();
    const [isTableMenuOpen, setIsTableMenuOpen] = useState(false);
    const [isImageMenuOpen, setIsImageMenuOpen] = useState(false);
    const { templateDropdownOptionsRef, viewedTemplateBlobName, isBound } = useTemplateDropdownOptions(doc, version);
    const { options, refTreeHeadings, resolveCaptionLabel } = useOptions(
      doc,
      isEdit,
      refEditor,
      version,
      setTreeHeadings,
      setCaptionsMap,
      setIsTableMenuOpen,
      setIsImageMenuOpen,
      templateDropdownOptionsRef
    );
    const editor = useEditor(options);
    refEditor.current = editor;
    const isAiEditReviewLocked = useEditorState({
      editor,
      selector: ({ editor: currentEditor }) => (currentEditor ? isAiEditReviewActive(currentEditor.state) : false),
    });

    const consumeAiEditSelectionReminder = useCallback(() => {
      if (refAiEditReminderDocId.current === doc._id) return false;
      refAiEditReminderDocId.current = doc._id;
      return true;
    }, [doc._id]);

    useEffect(() => {
      if (!isEdit) refAiEditReminderDocId.current = undefined;
    }, [isEdit]);

    // document content:
    const fieldValuesBlobName = version?.templateSource?.fieldValuesBlobName;

    // For bound docs, only pass blobName when templateBlobInfo is available.
    // Without it (template deleted/version missing), fetching the raw JSON would produce a non-string
    // result and leave contentStatus stuck at 'LOADING' forever.
    // For regular docs, use the HTML blob name.
    const contentBlobName = isBound
      ? viewedTemplateBlobName
        ? fieldValuesBlobName
        : undefined
      : variant?.blobName || version?.blobName;
    const refContentNode = useRef<TScrollbarElem>(null);

    // request edit:
    const [lockedState, setLockedState] = useState<TLockedState | undefined>();
    const [isCompleteOpen, setIsCompleteOpen] = useState<boolean>(false);
    const refAutoFocusCancelled = useRef(false);

    const { wasViewMode } = useHandleRequestDraft({
      doc,
      permissions,
      editor,
      requestEdit,
      setLockedState,
      connect,
      templateBlobInfo: isBound ? doc.templateBlobInfo : undefined,
    });

    const { content, contentStatus } = useContent({
      editor,
      refAutoFocusCancelled,
      doc,
      version,
      isEdit,
      retried,
      refContentNode,
      token: permissions.token,
      containerName: doc._id,
      blobName: contentBlobName,
      blocked: wasViewMode && requestEdit,
      templateBlobInfo: isBound ? doc.templateBlobInfo : undefined,
      templateBlobName: viewedTemplateBlobName,
    });
    // Read on the stored html, before the editor parses it - the parse drops what it cannot keep and
    // says nothing, and turning Tiptap's own check on makes a part swap throw instead.
    const hasUnsupportedContent = useMemo(() => hasUnsupportedMarkup(content), [content]);
    const logMutation = useLog();
    useEffect(() => {
      if (!hasUnsupportedContent) return;
      show('error', GENERIC_ERROR_MESSAGE);
      logMutation.mutate({ message: `UNSUPPORTED_MARKUP documentId=${doc._id} versionId=${versionId}` });
    }, [hasUnsupportedContent]);

    // Completion can keep the draft's blob and HTML, so the loader does not reload it.
    // Track the content source, not the version label, to preserve its ready state.
    const contentSourceKey = JSON.stringify([doc._id, variant?.folder, contentBlobName, viewedTemplateBlobName]);
    const { isContentReady, acknowledgeSavedContent } = useEditorContentReady(editor, contentSourceKey, content);

    // auto save:
    const { save, isExplicitSavePending } = useAutoSave(
      doc,
      isEdit,
      requestEdit,
      refBlockAutoSave,
      Boolean(transitioningVariant),
      refTreeHeadings,
      editor,
      acknowledgeSavedContent,
      variant,
      templateDropdownOptionsRef
    );

    const isSaved = useDocumentSavingStore((state) => state.isSaved);
    const subscribePendingOperations = useCallback(
      (notify: () => void) => (editor ? subscribePendingEditorOperations(editor, notify) : () => {}),
      [editor]
    );
    const hasPendingOperations = useSyncExternalStore(subscribePendingOperations, () =>
      Boolean(isSaved && editor && hasPendingEditorOperations(editor, doc._id, requestEdit))
    );
    // A Link does not fire beforeunload. Keep the route blocked until the user confirms and the same
    // save used by autosave has completed; a failed save leaves both the document and route intact.
    useLeaveGuard(isContentReady && (!isSaved || hasPendingOperations), save);

    // Used by changeVariant to fetch variant HTML blobs directly (regular docs only).
    const getBlob = useGetContent();

    // edit methods:
    const editDraft = useCallback(() => {
      if (replica) show('error', `Cannot edit a ${TERMINOLOGY.synchronized.lower} document.`);
      else {
        refAutoFocusCancelled.current = false;
        // A page that keeps `autoEdit` in the URL must not retry a document someone else holds.
        refAutoEditStarted.current = true;
        changeMany({ view: 'edit', [AUTO_EDIT_QUERY_PARAM]: undefined });
      }
    }, []);

    // With neither parameter left, the page's edit intent has ended, so its next one starts editing again.
    useEffect(() => {
      if (!autoEdit && view !== 'edit') refAutoEditStarted.current = false;
    }, [autoEdit, view]);

    // False once this session has tried, e.g. on a document someone else holds, though `autoEdit` stays in the URL.
    const isAutoEditPending =
      autoEdit &&
      !refAutoEditStarted.current &&
      !isEdit &&
      !replica &&
      !hasUnsupportedContent &&
      contentStatus !== 'ERROR';
    const refIsAutoEditPending = useRef(isAutoEditPending);
    refIsAutoEditPending.current = isAutoEditPending;

    useEffect(() => {
      if (!isAutoEditPending || requestEdit || !isContentReady) return;
      editDraft();
    }, [isAutoEditPending, editDraft, requestEdit, isContentReady]);

    const viewMode = useCallback(async () => {
      console.log('Switching to view mode...');
      refBlockAutoSave.current = true;
      const success = await save();
      if (success) {
        disconnect();
      } else {
        console.warn('Failed to save before switching to view mode.');
        refBlockAutoSave.current = false;
      }
      return success;
    }, []);

    // Not awaited: the caller opens its note dialog at once, and completeWhileEditing waits for this save.
    const prepareComplete = useCallback(() => {
      refAutoFocusCancelled.current = true;
      if (isEdit) void save({ forceSave: doc.versions.length <= 0 });
    }, [isEdit, doc.versions.length]);

    const completeVersion = useCallback(() => {
      prepareComplete();
      setIsCompleteOpen(true);
    }, [prepareComplete]);

    const refIsCompleting = useRef(false);
    const completeWhileEditing = useCallback(
      async <T,>(request: (editingDocumentId?: string) => Promise<T>): Promise<T> => {
        if (refIsCompleting.current) throw new Error('Completing is already running.');
        if ((refRequestEdit.current || refIsAutoEditPending.current) && !refIsEdit.current) {
          show('error', COMPLETE_WHILE_CONNECTING_FAILED_MESSAGE);
          throw new Error('Completing while the editor connects.');
        }
        refIsCompleting.current = true;
        try {
          const isEditing = refIsEdit.current;
          // Queued behind the save that prepareComplete started, so the version gets the latest content.
          if (isEditing && !(await save())) throw new Error('Saving before completing failed.');
          // Blocked before the request, so no autosave reaches the draft it completes.
          if (isEditing) refBlockAutoSave.current = true;
          let result: T;
          try {
            result = await request(isEditing ? refDoc.current._id : undefined);
          } catch (e) {
            if (isEditing) refBlockAutoSave.current = false;
            throw e;
          }
          if (isEditing) disconnect();
          return result;
        } finally {
          refIsCompleting.current = false;
        }
      },
      []
    );

    useEffect(() => {
      if (isEdit && retried) save({ isAutoSave: true }); // reconnected
    }, [isEdit, retried]);

    useEffect(() => {
      const shouldBeEditable = isEdit && !isExplicitSavePending && !isAiEditReviewLocked && !hasUnsupportedContent;
      if (editor && editor.isEditable !== shouldBeEditable) {
        editor.setEditable(shouldBeEditable);
        setTimeout(() => {
          toggleTableResizing(editor, shouldBeEditable);
        }, 10);
      }
    }, [isEdit, isExplicitSavePending, isAiEditReviewLocked, hasUnsupportedContent, Boolean(editor?.isEditable)]);

    // Sync editor storage with document landscape mode
    useEffect(() => {
      if (editor?.storage.section) {
        const isLandscape = Boolean(version?.config?.landscape);
        if (editor.storage.section.isLandscape !== isLandscape) {
          editor.commands.setLandscapeMode(isLandscape);
        }
      }
    }, [editor, version?.config?.landscape]);

    // change variant:
    const refVariant = useRef(variant);
    refVariant.current = variant;
    const changeVariant = useCallback(
      async (newVariant?: TVariant) => {
        if (!requestEdit) return change('variant', newVariant?.folder);

        if (!(await save())) throw new Error('Failed to save before changing variant.');

        setTransitioningVariant(newVariant?.folder || 'default');
        try {
          if (refVariant.current === newVariant) return setTransitioningVariant(null);

          const blobNameNew = newVariant?.blobName || version?.blobName;
          if (!blobNameNew) {
            show('error', 'Failed to load variant.');
            return;
          }
          const newContent = await getBlob.mutateAsync({
            token: permissions.token,
            containerName: doc._id,
            blobName: blobNameNew,
          });

          if (!editor) throw new Error('Editor is not ready.');

          // A variant is a different whole document, so it loads the same way - in parts when it is
          // large. loadDocument clears history itself for a large document, which is why the reset
          // below stays: it is what a whole-document load needs.
          loadDocument({
            editor,
            doc,
            version,
            html: insertSpaceIntoEmptyTags(newContent),
            onLoaded: () => {
              // clear history:
              editor.unregisterPlugin('history');
              editor.registerPlugin(history());
              change('variant', newVariant?.folder);
            },
          });
        } catch (e) {
          setTransitioningVariant(null);
          show('error', 'Failed to load variant.');
        }
      },
      [requestEdit, version?.blobName]
    );
    useEffect(() => {
      if (transitioningVariant) setTransitioningVariant(null);
    }, [variant]);

    // other:
    const [refTooltip, setRefTooltip] = useState<HTMLElement | null>(null);

    /**
     * The editor-built caption map covers whatever the editor holds - one part of a large document -
     * so a reference to a caption elsewhere would render "Loading..." forever and the insert-reference
     * picker would offer only the live part's captions. The whole-document map fills in the rest; the
     * editor's own entries win, because they include edits not committed yet.
     */
    const wholeCaptions = useWholeDocumentCaptions(editor);
    const mergedCaptionsMap = useMemo(
      () => (wholeCaptions ? { ...wholeCaptions, ...captionsMap } : captionsMap),
      [wholeCaptions, captionsMap]
    );

    const contextValue = useMemo(
      () => ({
        doc,
        permissions,
        resolvedItems,
        resolvedItemNames,

        replica,

        editor: editor as Editor,
        captionsMap: mergedCaptionsMap,
        wholeCaptionsReady: wholeCaptions !== null,
        resolveCaptionLabel,
        treeHeadings,
        content,
        contentStatus,
        hasUnsupportedContent,
        isContentReady,
        refContentNode,
        refTooltip,
        version,
        versionId,
        variant,

        isEdit,
        requestEdit,
        isAutoEditPending,
        hasDraft: Boolean(doc.draft),
        save,
        isExplicitSavePending,
        editDraft,
        viewMode,
        completeVersion,
        prepareComplete,
        completeWhileEditing,
        changeVariant,

        consumeAiEditSelectionReminder,

        isTableMenuOpen,
        setIsTableMenuOpen,

        isImageMenuOpen,
        setIsImageMenuOpen,

        openDocumentConfigRef,

        templateDropdownOptionsRef,
      }),
      [
        doc,
        permissions,
        resolvedItems,
        resolvedItemNames,
        replica,
        editor,
        mergedCaptionsMap,
        wholeCaptions,
        resolveCaptionLabel,
        treeHeadings,
        content,
        contentStatus,
        hasUnsupportedContent,
        isContentReady,
        refContentNode,
        refTooltip,
        version,
        versionId,
        variant,
        isEdit,
        requestEdit,
        isAutoEditPending,
        isTableMenuOpen,
        isImageMenuOpen,
        isExplicitSavePending,
        // These functions are using useCallback and won't change on every render.
        save,
        editDraft,
        viewMode,
        completeVersion,
        prepareComplete,
        completeWhileEditing,
        changeVariant,
        consumeAiEditSelectionReminder,
      ]
    );

    const renderDocumentContainer = () => (
      <DocumentContext.Provider value={contextValue}>{children ?? <DocumentContainer />}</DocumentContext.Provider>
    );

    const classes = useStyles();

    return (
      <>
        <div className={classes.tooltipContainer}>
          <div ref={setRefTooltip} className={classes.tooltip} />
        </div>
        <LockedDialog state={lockedState} setState={setLockedState} />
        <CreateVersionDialog
          isOpen={isCompleteOpen}
          setIsOpen={setIsCompleteOpen}
          item={doc}
          resolvedItems={resolvedItems}
          permissions={permissions}
          completeWhileEditing={completeWhileEditing}
        />
        <StatusLabel data={Boolean(editor)} loadingMessage="Loading document..." render={renderDocumentContainer} />
      </>
    );
  }
);
